import { useQuery } from "@tanstack/react-query";

import { SalesMeetingSaleStatus, SalesMeetingStatus } from "@fitness-app/data-models/entities/SalesMeeting";

import { type ResolvedRange } from "~/modules/Dashboard/AppStats/hooks/comparisonRange";
import { supabase } from "~/store/initializeStore";

export interface SalesFunnelCounts {
  /** Qualification-form leads (`signup_lead`), captured before any booking exists. */
  leads: number;
  /** Calendly calls in the window; a rescheduled row is a duplicate of the call it moved to. */
  meetings: number;
  attended: number;
  /** Attended calls the seller marked as sold. */
  won: number;
}

const HEAD_COUNT = { count: "exact", head: true } as const;

/**
 * Four count-only requests instead of pulling rows: a 90-day window holds a couple of thousand
 * meetings, more than PostgREST returns in one page.
 */
const fetchFunnel = async ([start, end]: ResolvedRange): Promise<SalesFunnelCounts> => {
  const from = start.toISOString();
  const to = end.toISOString();

  const meetingsInWindow = () =>
    supabase.from("sales_meeting").select("id", HEAD_COUNT).gte("startTime", from).lte("startTime", to);

  const [leads, meetings, attended, won] = await Promise.all([
    supabase.from("signup_lead").select("id", HEAD_COUNT).gte("createdAt", from).lte("createdAt", to).throwOnError(),
    meetingsInWindow().neq("status", SalesMeetingStatus.Rescheduled).throwOnError(),
    meetingsInWindow().eq("status", SalesMeetingStatus.Attended).throwOnError(),
    meetingsInWindow()
      .eq("status", SalesMeetingStatus.Attended)
      .eq("saleStatus", SalesMeetingSaleStatus.Yes)
      .throwOnError(),
  ]);

  return {
    leads: leads.count ?? 0,
    meetings: meetings.count ?? 0,
    attended: attended.count ?? 0,
    won: won.count ?? 0,
  };
};

const rangeKey = (range: ResolvedRange | null) => range?.map((date) => date.toISOString()) ?? null;

const useFunnelQuery = (scope: string, range: ResolvedRange | null) =>
  useQuery(["useSalesFunnelStats", scope, rangeKey(range)], {
    enabled: !!range,
    keepPreviousData: true,
    queryFn: () => fetchFunnel(range!),
  });

/** Lead → call → attended → sale counts for the selected range and its comparison window. */
export const useSalesFunnelStats = (range: ResolvedRange | null, comparisonRange: ResolvedRange | null) => {
  const current = useFunnelQuery("current", range);
  const previous = useFunnelQuery("previous", comparisonRange);

  return {
    current: current.data ?? null,
    previous: previous.data ?? null,
    isLoading: current.isLoading,
  };
};
